import React, { PureComponent } from 'react';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import { listCardFactory } from 'common/components/core/card/ListCard';
import MapBulkVerifyConfirmationDialog from 'common/components/zoneMap/MapBulkVerifyConfirmationDialog';
import MapBulkDeleteDialog from 'common/components/zoneMap/MapBulkDeleteDialog';
import { ButtonTypes, ButtonWithPopover } from 'common/components/core/button';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import VerifyButton from 'common/entity/generic/VerifyButton';
import { EditableInputField } from 'common/components/core/common/CustomEditableField';
import theme from './ZoneMapFolderCard.scss';
import compose from 'recompose/compose';
import withStateHandlers from 'recompose/withStateHandlers';
import { withHandlers, withPropsOnChange } from 'recompose';
import EventUtils from 'common/utils/EventUtils';

const EditableTitle = ({ t, theme, title, handleFolderNameUpdate }) => {
    return (
        <EditableInputField
            theme={theme}
            type="text"
            isEditingEnabled
            value={title}
            placeholder={t('zoneMap.untitled')}
            onSave={handleFolderNameUpdate}
        />
    );
};

const ExtendedRightIcons = ({
    theme,
    t,
    onVerify,
    onHide,
    onDelete,
    isVerified,
    isVerifyDisabled,
    isVerifyHidden,
    isHidden
}) => {
    let popOver;
    if (isVerifyDisabled) {
        popOver = t('zoneMap.noVerifyAccess');
    } else {
        popOver = t(
            isVerified ? 'zoneMap.unverifyFolder' : 'zoneMap.verifyFolder'
        ).concat('?');
    }
    return (
        <div className={theme.rightIcons}>
            <ButtonWithPopover
                className={theme.viewGroupBtn}
                icon={isHidden ? 'eye-hide' : 'eye'}
                onClick={onHide}
                invertPopover
                popOverContent={!isHidden ? t('zoneMap.hideFolder') : ''}
                place={PopoverPlaceEnum.Above}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
                buttonType={ButtonTypes.Icon}
            />

            <ButtonWithPopover
                className={theme.deleteBtn}
                icon="delete-bin"
                onClick={onDelete}
                invertPopover
                popOverContent={t('zoneMap.deleteFolder')}
                place={PopoverPlaceEnum.Above}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
                buttonType={ButtonTypes.Icon}
            />

            {!isVerifyHidden ? (
                <VerifyButton
                    theme={theme}
                    disabled={isVerifyDisabled}
                    isVerified={isVerified}
                    onVerify={onVerify}
                    noLabel
                    popOver={popOver}
                />
            ) : null}
        </div>
    );
};

const FolderCard = listCardFactory({
    Title: EditableTitle,
    RightIcon: ExtendedRightIcons
});

class ZoneMapFolderCard extends PureComponent {
    handleClick = () => {
        const { folder, onClick, onHide } = this.props;
        const FolderCardClickHandler = !folder.isHidden() ? onClick : onHide;
        FolderCardClickHandler({ folder });
    };

    handleHide = e => {
        const { folder, onHide } = this.props;
        EventUtils.stopPropagation(e);
        onHide({ folder });
    };

    handleDeleteClick = () => {
        const { geometriesIncluded, setBulkDeleteDialogActive } = this.props;
        if (geometriesIncluded && geometriesIncluded.size > 0) {
            setBulkDeleteDialogActive();
        } else {
            this.handleDelete();
        }
    };

    handleDelete = () => {
        const { folder, onDelete } = this.props;
        onDelete({ folder });
    };

    handleFolderNameUpdate = name => {
        const { folder, handleFolderNameUpdate } = this.props;
        return (
            handleFolderNameUpdate &&
            handleFolderNameUpdate(folder.setName(name))
        );
    };

    onVerify = e => {
        EventUtils.stopPropagation(e);
        this.props.onBulkVerifyToggle();
    };

    onMouseEnter = () => {
        const {
            folder,
            geometriesIncluded,
            handleFolderCardHover
        } = this.props;
        handleFolderCardHover({ folder, geometriesIncluded });
    };

    onMouseLeave = () => {
        const { handleFolderCardBlur } = this.props;
        handleFolderCardBlur();
    };

    render() {
        const {
            t,
            geometriesIncluded = [],
            className,
            folder,
            isHidden,
            isExpanded,
            isSelected,
            isVerified,
            isBulkVerificationDialogActive,
            onBulkVerifyToggle,
            setBulkVerifyDialogInactive,
            isBulkDeleteDialogActive,
            setBulkDeleteDialogInactive,
            isVerifyDisabled,
            isHoveredDuringDrag,
            children
        } = this.props;
        const { name } = folder;
        let isVerifyHidden = false;
        if ((isVerifyDisabled && !isVerified) || !geometriesIncluded.size > 0) {
            isVerifyHidden = true;
        }
        return (
            <div className={theme.folderCardWrap}>
                <FolderCard
                    t={t}
                    title={name}
                    subtitle={t('zoneMap.itemsIncluded_interval', {
                        postProcess: 'interval',
                        count: geometriesIncluded.size
                    })}
                    leftIcon={isExpanded ? 'folder-open' : 'folder-close'}
                    theme={theme}
                    className={classnames(theme.folderCard, {
                        [className]: !!className,
                        [theme.expanded]: isExpanded,
                        [theme.hidden]: folder.isHidden(),
                        [theme.dragBoundary]: isHoveredDuringDrag
                    })}
                    handleFolderNameUpdate={this.handleFolderNameUpdate}
                    onClick={this.handleClick}
                    onHide={this.handleHide}
                    onDelete={this.handleDeleteClick}
                    onVerify={this.onVerify}
                    isVerifyDisabled={isVerifyDisabled}
                    isVerifyHidden={isVerifyHidden}
                    isSelected={isSelected}
                    isVerified={isVerified}
                    isHidden={isHidden}
                    onMouseEnter={this.onMouseEnter}
                    onMouseLeave={this.onMouseLeave}
                />
                {isBulkVerificationDialogActive ? (
                    <MapBulkVerifyConfirmationDialog
                        type={t('zoneMap.folder')}
                        theme={theme}
                        active={isBulkVerificationDialogActive}
                        onAccept={onBulkVerifyToggle}
                        onCancel={setBulkVerifyDialogInactive}
                        isVerified={isVerified}
                    />
                ) : null}
                {isBulkDeleteDialogActive ? (
                    <MapBulkDeleteDialog
                        theme={theme}
                        active={isBulkDeleteDialogActive}
                        onAccept={this.handleDelete}
                        onCancel={setBulkDeleteDialogInactive}
                        count={geometriesIncluded.size}
                    />
                ) : null}
                {isExpanded ? children : []}
            </div>
        );
    }
}

const ZoneMapFolderCardWithHandlers = compose(
    withStateHandlers(
        () => {
            return {
                isBulkVerificationDialogActive: false,
                isBulkDeleteDialogActive: false
            };
        },
        {
            setBulkVerifyDialogActive: () => () => {
                return { isBulkVerificationDialogActive: true };
            },
            setBulkVerifyDialogInactive: () => () => {
                return { isBulkVerificationDialogActive: false };
            },
            setBulkDeleteDialogActive: () => () => {
                return { isBulkDeleteDialogActive: true };
            },
            setBulkDeleteDialogInactive: () => () => {
                return { isBulkDeleteDialogActive: false };
            }
        }
    ),
    withPropsOnChange(['geometriesIncluded'], ({ geometriesIncluded }) => {
        const isVerified =
            geometriesIncluded && geometriesIncluded.size
                ? geometriesIncluded.reduce(
                      (isVerified, geometry) =>
                          isVerified && geometry.isVerified(),
                      true
                  )
                : false;
        return {
            isVerified
        };
    }),
    withHandlers({
        onBulkVerifyToggle: ({
            folder,
            isVerified,
            isBulkVerificationDialogActive,
            setBulkVerifyDialogActive,
            setBulkVerifyDialogInactive,
            handleFolderVerifyToggle
        }) => () => {
            if (!isBulkVerificationDialogActive) {
                setBulkVerifyDialogActive();
                return;
            }
            handleFolderVerifyToggle({ folder, isVerified });
            setBulkVerifyDialogInactive();
        }
    })
)(ZoneMapFolderCard);
export default translate('entityManagement')(ZoneMapFolderCardWithHandlers);
